import React, { useEffect, useMemo, useState } from "react";
import Pagination from "./Pagination";
import "../styles/TableSystem.css";

const DEFAULT_ITEMS_PER_PAGE = 10;

function normalizeSortValue(value) {
  if (value == null) return "";
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (value instanceof Date) return value.getTime();

  const text = String(value).trim();
  if (!text) return "";

  const numeric = Number(text.replace(/,/g, ""));
  if (!Number.isNaN(numeric) && text.match(/^[-+]?\d[\d,.]*$/)) {
    return numeric;
  }

  const parsedDate = Date.parse(text);
  if (!Number.isNaN(parsedDate) && text.match(/\d{4}|\d{1,2}[/-]\d{1,2}/)) {
    return parsedDate;
  }

  return text.toLowerCase();
}

function getCellValue(row, column) {
  if (typeof column.getValue === "function") return column.getValue(row);
  if (column.key) return row[column.key];
  return null;
}

export default function DataTable({
  columns,
  data,
  rowKey = "id",
  emptyMessage = "No records found.",
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  initialSortKey = null,
  initialSortDirection = "asc",
  tableClassName = "",
  wrapperClassName = "",
  dense = false,
}) {
  const sortableColumns = columns.filter((column) => column.sortable !== false);
  const defaultSortKey =
    initialSortKey || sortableColumns.find((column) => column.key)?.key || null;

  const [sortConfig, setSortConfig] = useState({
    key: defaultSortKey,
    direction: initialSortDirection,
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  const sortedData = useMemo(() => {
    const rows = Array.isArray(data) ? [...data] : [];
    if (!sortConfig.key) return rows;

    const column = columns.find((item) => item.key === sortConfig.key);
    if (!column) return rows;

    rows.sort((left, right) => {
      const leftValue = normalizeSortValue(
        typeof column.getSortValue === "function"
          ? column.getSortValue(left)
          : getCellValue(left, column)
      );
      const rightValue = normalizeSortValue(
        typeof column.getSortValue === "function"
          ? column.getSortValue(right)
          : getCellValue(right, column)
      );

      if (leftValue < rightValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (leftValue > rightValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return rows;
  }, [columns, data, sortConfig]);

  const paginatedData = useMemo(() => {
    if (!itemsPerPage) return sortedData;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, itemsPerPage, sortedData]);

  const toggleSort = (column) => {
    if (column.sortable === false) return;

    setSortConfig((current) => {
      if (current.key === column.key) {
        return {
          key: column.key,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key: column.key,
        direction: "asc",
      };
    });
  };

  return (
    <div className={`nw-data-table-shell ${wrapperClassName}`.trim()}>
      <div className="table-responsive nw-data-table-responsive">
        <table
          className={`table align-middle mb-0 nw-data-table ${
            dense ? "nw-data-table-dense" : ""
          } ${tableClassName}`.trim()}
        >
          <thead>
            <tr>
              {columns.map((column) => {
                const isSorted = sortConfig.key === column.key;
                const sortable = column.sortable !== false;

                return (
                  <th
                    key={column.key || column.label}
                    className={`${column.headerClassName || ""} ${
                      sortable ? "is-sortable" : "is-static"
                    }`.trim()}
                    style={column.width ? { width: column.width } : undefined}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className="nw-sort-button"
                        onClick={() => toggleSort(column)}
                      >
                        <span>{column.label}</span>
                        <span
                          className={`nw-sort-indicator ${
                            isSorted ? `is-${sortConfig.direction}` : ""
                          }`}
                          aria-hidden="true"
                        >
                          <i className="fa-solid fa-arrow-up" />
                        </span>
                      </button>
                    ) : (
                      <span className="nw-static-header">{column.label}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center text-muted py-4">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => {
                const resolvedKey =
                  typeof rowKey === "function"
                    ? rowKey(row, index)
                    : row?.[rowKey] ?? `${currentPage}-${index}`;

                return (
                  <tr key={resolvedKey}>
                    {columns.map((column) => (
                      <td
                        key={column.key || column.label}
                        className={column.cellClassName || ""}
                      >
                        {typeof column.render === "function"
                          ? column.render(
                              row,
                              index + (currentPage - 1) * itemsPerPage
                            )
                          : getCellValue(row, column) ?? ""}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {itemsPerPage && sortedData.length > 0 ? (
        <div className="nw-data-table-footer">
          <Pagination
            currentPage={currentPage}
            totalItems={sortedData.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      ) : null}
    </div>
  );
}
